import { useState } from "react";
import { Text } from "@foris/avocado-suite";
import { useTranslation } from "react-i18next";
import Modal from "@components/modal";
import { downloadBlob } from "@utils/downloadBlob";
import { requestLinkLogReport } from "@modules/history-changes/link/utils/linkLogReport.util";

type ReportStatus = "confirm" | "loading" | "ready" | "error";

interface LinkLogReportModalProps {
  isOpen: boolean;
  /** Full report URL, including the `filter` query param */
  reportUrl: string;
  /** Label of the selected date range, shown to the user */
  periodLabel: string;
  onClose: () => void;
  /** Called once the report was handed to the browser */
  onDownloaded: () => void;
}

/**
 * Generates the link change history report and hands it to the user.
 *
 * The backend decides how the report is generated: a Piper signed URL is offered
 * with a download button, a legacy file is downloaded right away, and a failed
 * request falls back to opening the report URL like the previous flow.
 *
 * A click outside does not close it, so a ready signed URL is not lost by accident.
 */
const LinkLogReportModal = ({
  isOpen,
  reportUrl,
  periodLabel,
  onClose,
  onDownloaded,
}: LinkLogReportModalProps) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<ReportStatus>("confirm");
  const [signedUrl, setSignedUrl] = useState("");

  /**
   * Requests the report and moves the modal to the state that matches the response.
   *
   * Any unexpected failure ends in the error state, so the modal never stays locked
   * in loading.
   */
  const handleGenerate = async () => {
    setStatus("loading");

    try {
      const result = await requestLinkLogReport(reportUrl);

      if (result.type === "url") {
        setSignedUrl(result.url);
        setStatus("ready");
        return;
      }

      if (result.type === "file") {
        downloadBlob(result.blob, result.fileName);
        onDownloaded();
        onClose();
        return;
      }

      if (result.type === "network") {
        window.open(reportUrl, "_blank");
        onClose();
        return;
      }

      setStatus("error");
    } catch {
      setStatus("error");
    }
  };

  const handleDownload = () => {
    window.open(signedUrl, "_blank");
    onDownloaded();
    onClose();
  };

  const isLoading = status === "loading";

  const contentByStatus: Record<ReportStatus, string> = {
    confirm: t("link-changes-history.report-modal.description", { period: periodLabel }),
    loading: t("link-changes-history.report-modal.loading"),
    ready: t("link-changes-history.report-modal.ready"),
    error: t("link-changes-history.report-modal.error"),
  };

  const primaryButtonByStatus: Record<ReportStatus, { label: string; onClick: () => void }> = {
    confirm: {
      label: t("link-changes-history.report-modal.btn-generate"),
      onClick: handleGenerate,
    },
    loading: {
      label: t("link-changes-history.report-modal.btn-generate"),
      onClick: handleGenerate,
    },
    ready: { label: t("link-changes-history.report-modal.btn-download"), onClick: handleDownload },
    error: { label: t("generals.close"), onClick: onClose },
  };

  return (
    <Modal
      typeState={status === "error" ? "error" : "confirm"}
      show={isOpen}
      title={t("link-changes-history.report-modal.title")}
      textButtonPrincipal={primaryButtonByStatus[status].label}
      onClickPrincipal={primaryButtonByStatus[status].onClick}
      isLoading={isLoading}
      isDisabledButtonPrincipal={isLoading}
      textButtonSecondary={status === "error" ? undefined : t("generals.cancel")}
      onClickSecondary={onClose}
      onClose={onClose}
      disableOverlayClick
    >
      <Text>{contentByStatus[status]}</Text>
    </Modal>
  );
};

export default LinkLogReportModal;
